import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { FaEdit, FaEye } from 'react-icons/fa';
import PageForm from '../../templates/PageForm';
import TextEdit from '../../molecules/TextEdit';
import NewTooltip from '../../atoms/NewTooltip';
import PageSearch from '../../molecules/PageSearch';
import ATable, { ATableHeader } from '../../organisms/ATable';
import ScriptModal from './ScriptModal';
import useFetch from '../../../hooks/useFetch';
import appConfig from '../../../config/app-config';

const DATA_PROVIDER_ENV = appConfig.apis.dataProviderApiEnv;
const API_KEY = DATA_PROVIDER_ENV.key;
const BASE_URL = `${DATA_PROVIDER_ENV.address}/report-types`;

interface FieldItem {
    header: string;
    payloadKey: string;
    field: string;
    script?: string;
}

interface ReportType {
    id: string;
    name: string;
    provider: string;
    documentType: string;
    description: string;
    fields: FieldItem[];
    params: Record<string, string>;
    createdAt: string;
    status: boolean;
}

const ReportTypePageView: React.FC = () => {
    const { id = '' } = useParams();
    const navigate = useNavigate();

    const apiVersion = useSignal('');
    const reportType = useSignal<ReportType | null>(null);
    const fieldFilter = useSignal('');
    const showScriptModal = useSignal(false);
    const currentScript = useSignal('');

    const openScript = (script: string) => {
        currentScript.value = script;
        showScriptModal.value = true;
    };

    const fieldHeaders: ATableHeader[] = [
        { id: 'header', title: 'Cabeçalho da tabela', canSort: false },
        { id: 'field', title: 'Campo', canSort: false },
        { id: 'key', title: 'Campo no payload', canSort: false },
        { id: 'script', title: 'Script', canSort: false, width: '40px' },
    ];

    const { isLoading, data, error, isTokenError } = useFetch(`${BASE_URL}/${id}/page-view`, [
        { key: 'x-api-key', value: API_KEY },
    ]);

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;

            reportType.value = {
                id: data.id,
                name: data.name,
                documentType: data.documentType,
                provider: data.provider,
                description: data.description,
                fields: data.fields || [],
                params: data.params || {},
                createdAt: data.createdAt,
                status: data.active,
            };
        }
    }, [data]);

    return (
        <>
            <PageForm
                pageTitle={reportType.value?.name ?? ''}
                apiVersion={apiVersion.value}
                isLoading={isLoading}
                breadcrumb={[
                    { label: 'Tipos de relatório', url: '/data-provider/report-type/0/20/id/ASC/d=;' },
                    { label: `${reportType.value?.id ?? ''}`, url: '#' },
                ]}
                controlSlot={
                    <div className="flex space-x-2 justify-end">
                        <NewTooltip content="Voltar" alignment="bottom">
                            <button
                                type="button"
                                className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded"
                                onClick={() => navigate('/data-provider/report-type/0/20/id/ASC/d=;')}
                            >
                                <IoMdArrowRoundBack />
                            </button>
                        </NewTooltip>
                        <NewTooltip content="Editar registro" alignment="bottom">
                            <button
                                type="button"
                                className="text-white bg-blue-500 hover:bg-blue-700 font-medium rounded px-8 py-2.5"
                                onClick={() => navigate(`/data-provider/report-type/edit/${reportType.value?.id}`)}
                            >
                                <FaEdit />
                            </button>
                        </NewTooltip>
                    </div>
                }
            >
                {reportType.value && (
                    <div className="container mx-auto p-4 space-y-4">
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-2">
                                <TextEdit label="Provedor" value={reportType.value.provider} disabled />
                            </div>
                            <div className="col-span-2">
                                <TextEdit label="Tipo de documento:"
                                    value={reportType.value.documentType === 'ALL' ? 'CPF/CNPJ' : reportType.value.documentType === 'GOV_TAX_ID' ? 'CNPJ' : 'CPF'} disabled />
                            </div>
                            <div className="col-span-1 col-end-13">
                                <label className="block text-sm text-gray-500">Status</label>
                                <span
                                    className={`rounded px-2 py-1 text-xs font-bold text-white ${reportType.value.status ? 'bg-green-500' : 'bg-red-500'}`}
                                >
                                    {reportType.value.status ? 'Ativo' : 'Desativado'}
                                </span>
                            </div>
                        </div>
                        <div className="grid grid-cols-12 gap-4">
                            <div className="col-span-10">
                                <TextEdit label="Descrição" value={reportType.value.description} disabled />
                            </div>
                            <div className="col-span-2">
                                <TextEdit
                                    label="Criado em"
                                    value={new Date(reportType.value.createdAt).toLocaleDateString('pt-BR')}
                                    disabled
                                />
                            </div>
                        </div>
                        <hr />
                        <div>
                            <h3 className="font-semibold mb-2 align-middle">
                                <span className="bg-indigo-100 text-indigo-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-gray-700 dark:text-indigo-400 border border-indigo-400 ml-2">
                                    {Object.keys(reportType.value.params).length}
                                </span>
                                Parâmetros
                            </h3>
                            <div className="overflow-x-auto">
                                <table className="w-full table-auto border">
                                    <thead>
                                        <tr className="bg-slate-100">
                                            <th className="px-2 py-1 text-left border-b">Parâmetro</th>
                                            <th className="px-2 py-1 text-left border-b">Valor</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Object.entries(reportType.value.params).map(([key, value], index) => (
                                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                                <td className="px-2 py-1 border-b">{key}</td>
                                                <td className="px-2 py-1 border-b">{value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div>
                            <div className='grid grid-cols-2 gap-4'>
                                <h3 className="font-semibold mb-2 align-middle">
                                    <span className="bg-indigo-100 text-indigo-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-gray-700 dark:text-indigo-400 border border-indigo-400 ml-2">
                                        {reportType.value.fields.length}
                                    </span>
                                    Campos mapeados do payload
                                </h3>
                                <PageSearch
                                    value={fieldFilter.value}
                                    onSearch={(v) => (fieldFilter.value = v)}
                                    onChange={(v) => (fieldFilter.value = v)}
                                    filterCleanVisible={true}
                                    filterCleanEnabled={Boolean(fieldFilter.value)}
                                    onFilterClean={() => (fieldFilter.value = '')}
                                    configureVisible={false}
                                    label="Filtrar campos..."
                                    className="mb-2"
                                />
                            </div>
                            <ATable headers={fieldHeaders} autoResize={true} marginTop={440}>
                                {reportType.value.fields
                                    .filter((f) => {
                                        const value = fieldFilter.value.toLowerCase();
                                        if (!value) return true;
                                        return (
                                            f.header.toLowerCase().includes(value) ||
                                            f.field.toLowerCase().includes(value) ||
                                            (f.payloadKey || '').toLowerCase().includes(value) ||
                                            (f.script || '').toLowerCase().includes(value)
                                        );
                                    })
                                    .map((field, index) => (
                                        <tr key={index} className={`${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-slate-300 hover:shadow-md`}>
                                            <td className="px-2 py-1 border-b">{field.header}</td>
                                            <td className="px-2 py-1 border-b">{field.field}</td>
                                            <td className="px-2 py-1 border-b">{field.payloadKey}</td>
                                            <td className="px-2 py-1 border-b text-center">
                                                {field.script && (
                                                    <button
                                                        aria-label="Visualizar script"
                                                        className="text-blue-600"
                                                        onClick={() => openScript(field.script!)}
                                                    >
                                                        <FaEye />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                            </ATable>
                        </div>
                    </div>
                )}
            </PageForm>
            <ScriptModal
                id="script-modal"
                show={showScriptModal.value}
                script={currentScript.value}
                onClose={() => (showScriptModal.value = false)}
            />
        </>
    );
};

export default ReportTypePageView;
